import React, { useMemo, useState } from "react";
import { PageHead } from "@/components/PageHead";
import { Seo } from "@/components/Seo";
import {
  VIDEOS,
  YOUTUBE_PLAYLIST_URL,
  type VideoEntry,
} from "@/content/videos";
import type { LanguageBinding } from "@/hooks/useLanguage";
import { cx } from "@/lib/classnames";

/**
 * Renders a lightweight YouTube thumbnail + play button. The full iframe
 * is mounted only after the user clicks — so the /videos page loads fast
 * (one <img> per card) instead of spinning up 10 YouTube players on mount.
 *
 * Thumbnail source selection
 * --------------------------
 * YouTube serves several thumbnail resolutions per video, with quirks:
 *   - maxresdefault.jpg (1280x720, true 16:9, sharp) — only exists if
 *     the source was uploaded at ≥720p
 *   - hqdefault.jpg (480x360, 4:3 frame with black letterbox bars baked
 *     in for 16:9 content) — always exists, but produces ugly bars when
 *     displayed via object-cover at video aspect
 *   - mqdefault.jpg (320x180, true 16:9, no bars) — always exists; low-
 *     res but clean
 *
 * Strategy: start with maxresdefault for sharpness. When YouTube doesn't
 * have that resolution it serves a 120x90 grey placeholder back at HTTP
 * 200 (not a 404), so we detect that on load and fall back to mqdefault
 * which is guaranteed to be a real 16:9 image.
 */
const THUMB_GREY_PLACEHOLDER_WIDTH = 120;

const LazyYouTube: React.FC<{
  youtubeId: string;
  title: string;
  /** If set, use the explicit alt-frame thumbnail (skip maxresdefault). */
  thumbnailFrame?: 1 | 2 | 3;
}> = ({ youtubeId, title, thumbnailFrame }) => {
  const [playing, setPlaying] = useState(false);
  const initialThumb = thumbnailFrame
    ? `https://i.ytimg.com/vi/${youtubeId}/mq${thumbnailFrame}.jpg`
    : `https://i.ytimg.com/vi/${youtubeId}/maxresdefault.jpg`;
  const [thumbSrc, setThumbSrc] = useState(initialThumb);

  if (playing) {
    // Privacy-enhanced YouTube domain (no cookies set until the user
    // interacts with the player). The CSP frame-src directive in
    // public/_headers is correspondingly tight: only this exact origin
    // is allowed to be framed.
    return (
      <iframe
        className="h-full w-full"
        src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0`}
        title={title}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    );
  }

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    // YouTube's "resolution not available" placeholder is always 120x90.
    if (img.naturalWidth === THUMB_GREY_PLACEHOLDER_WIDTH) {
      setThumbSrc(`https://i.ytimg.com/vi/${youtubeId}/mqdefault.jpg`);
    }
  };

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      className="group relative h-full w-full cursor-pointer block"
      aria-label={title}
    >
      <img
        src={thumbSrc}
        alt={title}
        loading="lazy"
        decoding="async"
        onLoad={handleLoad}
        onError={() =>
          setThumbSrc(`https://i.ytimg.com/vi/${youtubeId}/mqdefault.jpg`)
        }
        className="h-full w-full object-cover"
      />
      {/* Play button overlay */}
      <span
        className="absolute inset-0 flex items-center justify-center transition-opacity group-hover:opacity-90"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,.15) 0%, rgba(0,0,0,.35) 70%)",
        }}
      >
        <span
          className="flex items-center justify-center h-16 w-16 md:h-20 md:w-20 rounded-full"
          style={{
            background: "rgba(200,68,30,.92)",
            boxShadow: "0 8px 24px rgba(0,0,0,.55)",
          }}
        >
          <svg
            width="22"
            height="26"
            viewBox="0 0 22 26"
            fill="#fff3df"
            aria-hidden
            style={{ marginLeft: 4 }}
          >
            <path d="M0 0 L22 13 L0 26 Z" />
          </svg>
        </span>
      </span>
    </button>
  );
};

const VideoRow: React.FC<{ v: VideoEntry; i: number; language: LanguageBinding }> = ({
  v,
  i,
  language,
}) => {
  const { t, lang, isRTL } = language;

  const langBadge =
    v.language === "en"
      ? isRTL ? "إنجليزي" : "English"
      : isRTL ? "عربي" : "Arabic";
  const dateLabel = v.date ? new Date(v.date).toISOString().slice(0, 10) : null;

  return (
    <article
      className="p-6 md:p-7"
      style={{
        border: "1px solid var(--hairline-dark)",
        background: "var(--card-surface)",
      }}
    >
      <div className="flex items-baseline gap-4 mb-4">
        <span className="cat-num text-lg" style={{ color: "var(--vermillion-2)" }}>
          {String(i + 1).padStart(2, "0")}
        </span>
        <span className="flex-1 h-px" style={{ background: "var(--hairline-dark)" }} />
        <span
          className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide", "text-[10px]")}
          style={{ color: "var(--muted-dark)" }}
        >
          {langBadge}
          {dateLabel && <span className="mx-2 opacity-60">·</span>}
          {dateLabel}
        </span>
      </div>

      <h3
        className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif", "text-xl md:text-2xl leading-snug")}
        style={{ color: "var(--parchment)", fontWeight: 600 }}
      >
        {v[lang].t}
      </h3>
      <div
        className={cx(isRTL ? "ff-ar-sans" : "ff-en-body", "mt-2 text-sm")}
        style={{ color: "var(--muted-dark)" }}
      >
        {v[lang].d}
      </div>

      {v.youtubeId ? (
        <div className="mt-5 aspect-video w-full overflow-hidden rounded-sm">
          <LazyYouTube
            youtubeId={v.youtubeId}
            title={v[lang].t}
            thumbnailFrame={v.thumbnailFrame}
          />
        </div>
      ) : (
        <div
          className="mt-5 p-4 text-sm"
          style={{
            border: "1px dashed var(--hairline-dark)",
            color: "var(--muted-dark)",
          }}
        >
          <span className={isRTL ? "ff-ar-sans" : "ff-en-body italic"}>
            {isRTL
              ? "سيُضاف مشغّل الفيديو فور إضافة معرّف يوتيوب للحلقة."
              : "The embedded player will appear once the YouTube ID is added."}
          </span>{" "}
          <a
            href={YOUTUBE_PLAYLIST_URL}
            target="_blank"
            rel="noreferrer"
            className="underline"
            style={{ color: "var(--vermillion-2)" }}
          >
            {t.videosPage.watchOnYoutube} ↗
          </a>
        </div>
      )}
    </article>
  );
};

export const Videos: React.FC<{ language: LanguageBinding }> = ({ language }) => {
  const { t, lang, isRTL } = language;

  const orderedVideos = useMemo(() => {
    const list = [...VIDEOS];
    list.sort((a, b) => {
      if (a.language !== b.language) return a.language === lang ? -1 : 1;
      return (b.date ?? "").localeCompare(a.date ?? "");
    });
    return list;
  }, [lang]);

  const featured = orderedVideos.find((v) => v.youtubeId) ?? orderedVideos[0];
  const rest = orderedVideos.filter((v) => v.slug !== featured?.slug);

  return (
    <>
      <Seo
        title={t.meta.videos.title}
        description={t.meta.videos.description}
        lang={lang}
        path="/videos"
      />
      <PageHead
        kicker={t.videosPage.kicker}
        title={t.videosPage.title}
        tagline={t.videosPage.intro}
        isRTL={isRTL}
      />

      <section className="relative" style={{ background: "var(--ink-2)" }}>
        <div className="grain" />
        <div className="max-w-page mx-auto px-6 md:px-10 py-20 md:py-24 relative">
          <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
            <div
              className={cx(
                isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest",
                "text-[11px]",
              )}
              style={{ color: "var(--vermillion-2)" }}
            >
              {t.videosPage.playlistLabel}
            </div>
            <a
              href={YOUTUBE_PLAYLIST_URL}
              target="_blank"
              rel="noreferrer"
              className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide", "text-[12px] underline")}
              style={{ color: "var(--vermillion-2)" }}
            >
              {t.videosPage.watchOnYoutube} ↗
            </a>
          </div>

          {featured?.youtubeId && (
            <div className="aspect-video w-full max-w-[900px] mx-auto mb-14 overflow-hidden rounded-sm">
              <LazyYouTube
                youtubeId={featured.youtubeId}
                title={featured[lang].t}
                thumbnailFrame={featured.thumbnailFrame}
              />
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            {rest.map((v, i) => (
              <VideoRow key={v.id} v={v} i={i} language={language} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
